/**
 * The bookkeeping behind a progress bar for a multi-step save.
 *
 * Provisioning a truck is up to a dozen dependent round trips — endpoints, a zone, a trigger per
 * alert, a re-read, the wiring — and a single spinner over all of them is indistinguishable from a
 * hung request on a slow link. The add-device wizard already reports each step as it lands; this
 * gives the device page the same courtesy, with a bar whose length is known before the first write
 * so it fills rather than crawls.
 *
 * Pure on purpose: the reducer here is what the tests hold still, and the React hook and dialog
 * that render it are thin wrappers. Nothing in this file knows what a step *does* — a caller plans
 * its steps by key, the work reports against those keys, and the bar is arithmetic over the result.
 */

export type ProgressStepState = "pending" | "active" | "done" | "skipped" | "failed";

export type ProgressStep = {
    key: string;
    label: string;
    state: ProgressStepState;
    /** Why a step was skipped or failed, in words a director can act on. */
    note?: string;
};

/** One report from the work: a step began, finished, was declined, or broke. */
export type ProgressEvent = {
    key: string;
    label: string;
    state: Exclude<ProgressStepState, "pending">;
    note?: string;
};

export type ProgressReporter = (event: ProgressEvent) => void;

export type PlannedStep = { key: string; label: string };

/** The plan as first drawn: everything pending, so the bar knows its length before work starts. */
export function planSteps(planned: PlannedStep[]): ProgressStep[] {
    return planned.map((p) => ({ key: p.key, label: p.label, state: "pending" }));
}

/**
 * Fold one event into the step list.
 *
 * A key the plan did not name is appended rather than dropped: work that knows more than the
 * planner did (it discovered a zone was missing) still gets its step drawn, at the cost of the bar
 * lengthening by one — better than a step that runs invisibly. A planned step keeps the planner's
 * label, because that wording is what the reader saw first.
 */
export function applyProgress(steps: ProgressStep[], event: ProgressEvent): ProgressStep[] {
    const at = steps.findIndex((s) => s.key === event.key);
    if (at < 0) return [...steps, { key: event.key, label: event.label, state: event.state, note: event.note }];
    return steps.map((s, i) => (i === at ? { ...s, state: event.state, note: event.note } : s));
}

/** Mark whatever is still running as failed — for the moment a throw escapes the run. */
export function failActive(steps: ProgressStep[], note: string): ProgressStep[] {
    return steps.map((s) => (s.state === "active" ? { ...s, state: "failed", note } : s));
}

const isFinished = (s: ProgressStep) => s.state === "done" || s.state === "skipped" || s.state === "failed";

/** How many steps are over, whatever their outcome. */
export function finishedCount(steps: ProgressStep[]): number {
    return steps.filter(isFinished).length;
}

/**
 * Finished steps over total, 0–1 — what the bar draws.
 *
 * Skipped and failed both count as finished: the bar measures how much of the plan has been
 * *settled*, not how much succeeded. A run that declines three of five steps is 60% resolved when
 * it declines them, not stuck at zero until the end.
 */
export function progressFraction(steps: ProgressStep[]): number {
    if (steps.length === 0) return 0;
    return finishedCount(steps) / steps.length;
}
